/*
defines funtionality for adding paths between nodes
*/

import {getPathBetweenConcepts} from '../utilities/conceptnet';

/**
 * defines functionality for making linear paths between nodes
 */
export default class PathController {
    /**
     * makes a new PathController given a graph controller
     * @param {GraphController} graphController
     */
    constructor(graphController) {
        this.graphController = graphController;

        this.makePathOfType = {
            'concept': (start, end) => this.makeConceptPath(start, end),
        };
    }

    /**
     * makes a new path between the two given nodes with the given type
     *
     * @param {NodeView} startNode where to start
     * @param {NodeView} endNode where to end
     * @param {String} [type] (optional) the sort of path to make. conceptnet, wikipedia, etc. defaults to conceptnet
     */
    makePathBetween(startNode, endNode, type = 'concept') {
        // by default makes a conceptnet path
        this.makePathOfType[type](startNode, endNode);
    }

    /**
     * checks if the given list of terms exist in concept net.
     * currently unused
     *
     * @param {Array<String>} terms
     * @param {Function} callback
     */
    checkIfConceptsExist(terms, callback) {
        let allExist = true;
        for (let i = 0; i < terms.length; i++) {
            const term = terms[i];
            let info = {
                url: `http://api.conceptnet.io/c/en/${term}`,
                request: {
                    limit: 1,
                },
                headers: {},
                remaining: terms.length,
            };
            let response = {};

            getNewDataFromAPI(info, response, function(res) {
                if (res.edges.length === 0) {
                    callback(allExist = false);
                }
                info.remaining -= 1;
                if (info.remaining <= 0 && allExist) {
                    callback(allExist);
                }
            });
        }
    }

    /**
     * makes a path of concepts between the given two nodes
     *
     * @param {NodeView} startNode
     * @param {NodeView} endNode
     */
    makeConceptPath(startNode, endNode) {
        getPathBetweenConcepts(
            startNode.node.title,
            endNode.node.title,
            (result) => {
              console.log("Number of Results " + result.length);
                var index = Math.floor(result.length*Math.random());
                console.log("Generated random index: " + index)
                let path = result[index];
                this.convertPathToNodes(startNode, endNode, path);
            }
        )
    }

    /**
     * given a list of string labels, with nodes and links alternating,
     * creates intermediary nodes in between the two given nodes.
     *
     * Note: The path array *must* start and end with a node, and always alternate nodes and links.
     * The link labels may be left blank if there is no information about this relationship.
     * Furthermore, the path must start with the given start node, and end with the given end node.
     * Therefore the path length must be odd.
     *
     * for example,
     * given the node
     * ConnectorNodeView for 'panacea'
     * and the node
     * ConnectorNodeView for 'aberration'
     * and the path
     * ['panacea', 'RelatedTo', 'medicine', 'HasContext', 'aberration']
     * 0, 1, 2, 3, 4, 5, 6
     * -     0     1     2
     * (5 - 3) / 2 = 2/2 = 1
     * ((5 + 1) / 2) - 1 = 4/2 = 2
     * ((3 + 1) / 2) - 1 = 2
     * 1 --> 0
     * 1
     *
     *
     * the function will produce:
     * the link of type RelatedTo between panacea and medecine
     * the link of type HasContext between medicine and aberration
     * the connector node labeled medicine
     *
     * Another example:
     * the path ['dog', '', 'cat']
     * will create
     * one link with no label from node 'dog' to node 'cat'
     *
     * @example
     * // makes 1 node and 2 links
     * convertPathToNodes2(DogNode, CatNode, ['cat', 'RelatedTo', 'animal', 'RelatedTo', 'dog'])
     * @param {NodeView} startNode the node to start at
     * @param {NodeView} endNode the node to end at
     * @param {String} path a list of strings labeling alternating nodes and links, starting and ending with a node
     */
    convertPathToNodes(startNode, endNode, path) {
        // first check for errors
        if (path.length % 2 === 0) {
            throw Error('invalid path length. must include the start and end, and nodes and links in between alternating. must be odd length.');
        }
        // keep track of nodes in path
        let nodesInPath = [];

        // make nodes from even items in path
        this.makeNodesInPath(startNode, endNode, path, nodesInPath);

        // make links from odd items in path
        this.makeLinksInPath(nodesInPath, path);
    }

    /**
     * makes new links in the path given a list of nodes and a list of labels
     *
     * @param {Array<NodeView>} nodesInPath
     * @param {Array<String>} path
     */
    makeLinksInPath(nodesInPath, path) {
        for (let index = 1; index < path.length - 1; index += 2) {
            let linkLabel = path[index];
            let source = nodesInPath[(index + 1) / 2 - 1];
            let target = nodesInPath[(index + 1) / 2];
            this.graphController.addLink(linkLabel, source.id, target.id);
        }
    }

    /**
     * given a path, adds new nodes to the given list
     *
     * @param {NodeView} startNode the first node
     * @param {NodeView} endNode the last node
     * @param {Array<String>} path the list of nodes and links
     * @param {Array<NodeView>} nodesInPath the current list of nodes made
     */
    makeNodesInPath(startNode, endNode, path, nodesInPath) {
        nodesInPath.push(startNode);

        // calculate distance between start and end
        // so we can linearly interpolate nodes in between
        let xDistance = (endNode.x - startNode.x);
        let yDistance = (endNode.y - startNode.y);
        let offset = Math.floor((Math.random() * 200) + 1);

        for (let index = 2; index < path.length - 2; index += 2) {
            let nodeLabel = path[index];
            let x = startNode.x + ((index + 1) / (path.length + 1)) * xDistance + offset;
            let y = startNode.y + ((index + 1) / (path.length + 1)) * yDistance + offset;
            let newNode = this.graphController.addConnectorNode(nodeLabel, x, y);
            nodesInPath.push(newNode);
        }

        nodesInPath.push(endNode);
    }
}
